const EXPECTED_TRPC_CODES = new Set([
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "BAD_REQUEST",
  "TOO_MANY_REQUESTS",
  "CONFLICT",
  "PRECONDITION_FAILED",
]);

/** Empty/truncated responses and offline fetches — not actionable server bugs. */
const TRANSIENT_TRANSPORT_MESSAGE =
  /json parse error|unexpected end of (?:json )?input|unexpected token|network request failed|failed to fetch|networkerror|load failed|the internet connection appears to be offline|a network error occurred|network connection was lost|timeout|timed out|gateway timeout/i;

/**
 * Expected product / client / infra noise that should not open Sentry issues.
 * Keep patterns tight — only messages we intentionally treat as non-bugs.
 */
const EXPECTED_NOISE_MESSAGE =
  /transcript is empty for video|idempotency key|auth session missing|manual linking is disabled|not allowed to make the purchase|billing[_ ]unavailable|purchasenotallowederror|issue with your configuration|offerings.?empty|none of the products registered|unable to open url|missing expo_public_supabase|unknown backend error|http 524|watchdogtermination|purchasewascancelled|user cancelled|purchase was cancelled|fetch failed|failed to connect|play method is not allowed|notallowederror|cannot assign to read only property 'push'|nativeresponse\.cancelstreaming|cannot be cast to type class expo\.modules\.fetch\.nativeresponse/i;

function readTrpcCode(error: object): string | undefined {
  if ("code" in error && typeof error.code === "string") {
    return error.code;
  }
  if (
    "data" in error &&
    error.data !== null &&
    typeof error.data === "object" &&
    "code" in error.data &&
    typeof error.data.code === "string"
  ) {
    return error.data.code;
  }
  return undefined;
}

function readMessage(error: object): string {
  if ("message" in error && typeof error.message === "string") {
    return error.message;
  }
  return "";
}

function isTransientTransportError(error: object): boolean {
  if (TRANSIENT_TRANSPORT_MESSAGE.test(readMessage(error))) {
    return true;
  }
  if (
    "cause" in error &&
    error.cause !== null &&
    typeof error.cause === "object"
  ) {
    return isTransientTransportError(error.cause);
  }
  return false;
}

/** Client-facing tRPC failures that should not create Sentry issues. */
export function isExpectedTrpcError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  if (isTransientTransportError(error)) {
    return true;
  }
  const code = readTrpcCode(error);
  return typeof code === "string" && EXPECTED_TRPC_CODES.has(code);
}

/** Broader filter for captureException / beforeSend (tRPC + operational noise). */
export function isExpectedNoiseError(error: unknown): boolean {
  if (isExpectedTrpcError(error)) {
    return true;
  }
  if (typeof error === "string") {
    return EXPECTED_NOISE_MESSAGE.test(error);
  }
  if (!error || typeof error !== "object") {
    return false;
  }
  if (EXPECTED_NOISE_MESSAGE.test(readMessage(error))) {
    return true;
  }
  if (
    "name" in error &&
    typeof error.name === "string" &&
    EXPECTED_NOISE_MESSAGE.test(error.name)
  ) {
    return true;
  }
  if (
    "cause" in error &&
    error.cause !== null &&
    typeof error.cause === "object"
  ) {
    return isExpectedNoiseError(error.cause);
  }
  return false;
}
